import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router';
import { useFeedback } from './feedback/Service';

/**
 * useUnsavedChangesGuard
 * Blocks navigation (browser back/forward + in-app anchor/link clicks) while `when` is true.
 * Shows an async confirmation via the provided `confirm` function.
 *
 * confirm(): (options) => Promise<boolean>
 * - Should resolve true to proceed, false to stay.
 *
 * Notes:
 * - Programmatic navigate() calls should clear `when` beforehand if they are safe.
 * - External links, new tab (meta/cmd click), modified clicks, hash changes, and same-page navigation are ignored.
 */
export default function useUnsavedChangesGuard(when) {
  const navigate = useNavigate();
  const feedback = useFeedback();
  const sentinelAddedRef = useRef(false);
  const allowNextPopRef = useRef(false);
  const confirmingRef = useRef(false);
  const activeRef = useRef(when);
  activeRef.current = when;

  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (when) {
        e.preventDefault();
        e.returnValue = '';
        return '';
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [when]);

  // Popstate guard (back/forward)
  useEffect(() => {
    if (!activeRef.current) {
      sentinelAddedRef.current = false;
      allowNextPopRef.current = false;
      confirmingRef.current = false;
      return;
    }

    if (!sentinelAddedRef.current) {
      try {
        window.history.pushState({ __unsavedSentinel: true }, '', window.location.href);
        sentinelAddedRef.current = true;
      } catch { /* ignore */ }
    }

    const onPopState = (e) => {
      if (!activeRef.current) return;
      if (allowNextPopRef.current) {
        allowNextPopRef.current = false;
        return;
      }
      try { window.history.pushState({ __unsavedSentinel: true }, '', window.location.href); } catch { /* ignore */ }
      if (confirmingRef.current) return;
      confirmingRef.current = true;
      feedback.confirm({ title: 'Unsaved Changes', text: 'You have unsaved changes. Leave without saving?' })
        .then(confirmed => {
          if (confirmed) {
            console.log('Unsaved changes confirmed, navigating away', e.state);
            activeRef.current = false; // mark as safe
            allowNextPopRef.current = true;
            if (e.state) {
              setTimeout(() => window.history.back(), 1);
              setTimeout(() => window.history.back(), 30);
            }
          }
        })
        .finally(() => { confirmingRef.current = false; });
    };

    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, [when, feedback]);

  // Anchor/link interception
  useEffect(() => {
    if (!activeRef.current) return;

    const isModified = (e) => e.metaKey || e.altKey || e.ctrlKey || e.shiftKey || e.button !== 0;

    const clickHandler = (e) => {
      if (!activeRef.current) return;
      if (confirmingRef.current) return;
      const anchor = e.target.closest('a');
      if (!anchor) return;
      const href = anchor.getAttribute('href');
      if (!href || href.startsWith('#')) return;
      if (anchor.target === '_blank') return;
      if (isModified(e)) return;
      const url = new URL(href, window.location.origin);
      if (url.origin !== window.location.origin) return;
      const currentFull = window.location.pathname + window.location.search + window.location.hash;
      const nextFull = url.pathname + url.search + url.hash;
      if (currentFull === nextFull) return;
      e.preventDefault();
      confirmingRef.current = true;
      feedback.confirm({ title: 'Unsaved Changes', text: 'You have unsaved changes. Leave without saving?' })
        .then(confirmed => {
          if (confirmed) {
            activeRef.current = false;
            navigate(nextFull);
          }
        })
        .finally(() => { confirmingRef.current = false; });
    };

    document.addEventListener('click', clickHandler, true);
    return () => document.removeEventListener('click', clickHandler, true);
  }, [navigate, when, feedback]);

  // beforeunload (refresh/close)
  useEffect(() => {
    const onBeforeUnload = (e) => {
      if (!activeRef.current) return;
      e.preventDefault();
      e.returnValue = '';
      return '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, []);
}
